// Dana "help me write" popover: proofread / rewrite the current draft in a chosen
// tone, preview the result, and use it (or dismiss). Opened from the composer's "+".
import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { assistDraft, type AssistTone } from '../draftAssist';

const TONES: { tone: AssistTone; label: string }[] = [
  { tone: 'improve', label: 'Improve' },
  { tone: 'formal', label: 'More formal' },
  { tone: 'shorter', label: 'Shorter' },
];

export const DanaAssist: React.FC<{ draft: string; onUse: (text: string) => void; onClose: () => void }> = ({
  draft,
  onUse,
  onClose,
}) => {
  const [loading, setLoading] = useState<AssistTone | null>(null);
  const [result, setResult] = useState('');
  const [error, setError] = useState(false);
  const empty = draft.trim().length === 0;

  const run = async (tone: AssistTone) => {
    setLoading(tone);
    setError(false);
    setResult('');
    try {
      const out = await assistDraft(tone, draft);
      if (out) setResult(out);
      else setError(true);
    } catch {
      setError(true);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="absolute bottom-full left-0 z-20 mb-2 w-80 rounded-xl border border-border bg-card p-3 shadow-lg">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-charcoal">
        <Sparkles className="h-3.5 w-3.5 text-primary-500" /> Dana — help me write
      </div>
      {empty ? (
        <p className="text-xs text-muted-foreground">Type a message first, then let Dana polish it.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {TONES.map((tn) => (
            <button
              key={tn.tone}
              type="button"
              onClick={() => run(tn.tone)}
              disabled={loading !== null}
              className="rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-charcoal hover:bg-muted disabled:opacity-50"
            >
              {loading === tn.tone ? '…' : tn.label}
            </button>
          ))}
        </div>
      )}
      {error && <p className="mt-2 text-xs text-red-600">Dana couldn’t help right now — try again.</p>}
      {result && (
        <div className="mt-2">
          <p className="whitespace-pre-wrap rounded-lg bg-muted p-2 text-sm text-charcoal">{result}</p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => {
                onUse(result);
                onClose();
              }}
              className="rounded-lg bg-primary-500 px-3 py-1 text-xs font-semibold text-white hover:bg-primary-600"
            >
              Use it
            </button>
            <button type="button" onClick={onClose} className="rounded-lg px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
